/**
 * ProcessLog 工厂与去重（F-M13 从 tagParser.ts 拆分）
 *
 * 提供 ProcessLogEntry 的创建、终态化与协作状态追加：
 *   - createLocalId         本地 ID 生成（基于时间戳 + 随机数）
 *   - createProcessLog      创建单条 log entry
 *   - finalizeProcessLogs   终态化（active → completed，清除 dedupKey）
 *   - addCollabStatusToLogs 按 phase+icon+label 去重追加协作状态 log
 */
import type { ProcessLogEntry, ProcessLogKind } from '@/components/agent/types';

/** 本地 ID 生成（被 createProcessLog 等多处使用） */
export const createLocalId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** 创建单条 ProcessLogEntry */
export const createProcessLog = (
  kind: ProcessLogKind,
  title: string,
  detail?: string,
  status: ProcessLogEntry['status'] = 'completed'
): ProcessLogEntry => ({
  id: createLocalId(),
  kind,
  status,
  title,
  detail,
});

/**
 * 终态化 processLogs：
 *   - 清除 dedupKey（不持久化到后端）
 *   - 把 active 状态改为 completed
 *   - "正在 xxx" 标题改为 "已完成 xxx"
 */
export const finalizeProcessLogs = (logs: ProcessLogEntry[]) =>
  logs.map((log) => {
    // 🔴 v5-#14: 清除 dedupKey 内部字段，避免持久化到后端 metadata
    // 🔴 M18: dedupKey 已提升为 ProcessLogEntry 的可选字段，无需 as any
    const { dedupKey: _stripped, ...cleanLog } = log;
    if (log.status !== 'active') return cleanLog as ProcessLogEntry;
    return {
      ...cleanLog,
      status: 'completed' as const,
      title: log.title.startsWith('正在') ? log.title.replace(/^正在/, '已完成') : log.title,
    } as ProcessLogEntry;
  });

/**
 * 按 (phase + icon + label) 三元组去重追加协作状态 log
 * @returns { logs, wasNew } wasNew 表示是否实际新增了 log
 */
export const addCollabStatusToLogs = (
  parsed: { phase: string; icon: string; label: string; detail?: string },
  currentLogs: ProcessLogEntry[],
  overrideStatus?: ProcessLogEntry['status'],
): { logs: ProcessLogEntry[]; wasNew: boolean } => {
  const { phase, icon, label, detail } = parsed;
  const dedupKey = `collab_${phase}_${icon}_${label}`;
  if (currentLogs.some(l => l.dedupKey === dedupKey)) {
    return { logs: currentLogs, wasNew: false };
  }
  const log = createProcessLog('action', `${icon} ${label}`, detail, overrideStatus || (phase === 'executing' ? 'active' : 'completed'));
  log.dedupKey = dedupKey;
  return { logs: [...currentLogs, log], wasNew: true };
};
